import { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { formatInteger } from "../formatters";
import {
  BRAND_STAGES,
  StageCounts,
  formatLeadCount,
  formatOptionalPercent,
  formatOptionalSignedPercent,
  ofPreviousPercent,
  vsPreviousPercent,
} from "./brandStagesApiMapping";
import BrandStagesTooltipContent, { BRAND_STAGES_TOOLTIP_SLOT_PROPS } from "./BrandStagesTooltipContent";
import BrandStagesFunnelSkeleton from "./BrandStagesFunnelSkeleton";
import BrandStagesErrorBanner from "./BrandStagesErrorBanner";

// Paid's bar takes 82% of the track, leaving room for the value text.
const MAX_BAR_PERCENT = 82;
const MIN_BAR_PERCENT = 0.6;

function barWidthPercent(count: number, paid: number): number {
  if (!count || !paid) return 0;
  return Math.max(MIN_BAR_PERCENT, (count / paid) * MAX_BAR_PERCENT);
}

interface BrandStagesFunnelProps {
  counts: StageCounts | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  rangeLabel: string;
  onStageClick: (stageIndex: number) => void;
}

// Horizontal All-brands funnel (spec Section 3.3). Each bar's label is
// conversion from the PREVIOUS stage; the hover tooltip shows retention
// from Paid.
export default function BrandStagesFunnel({
  counts,
  isLoading,
  isError,
  onRetry,
  rangeLabel,
  onStageClick,
}: BrandStagesFunnelProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const renderBody = () => {
    if (isLoading) return <BrandStagesFunnelSkeleton />;
    if (isError || !counts) return null;

    const paid = counts[0];
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }} onMouseLeave={() => setHoveredIndex(null)}>
        {BRAND_STAGES.map((stage, index) => {
          const count = counts[index];
          const lost = index === 0 ? 0 : counts[index - 1] - count;
          const valueLabel =
            index === 0 ? "100% · entry point" : `${formatOptionalPercent(ofPreviousPercent(counts, index))} of previous stage`;

          return (
            <Box key={stage.step}>
              {index > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", alignItems: "center", height: 22 }}>
                  <Box />
                  <Box
                    sx={{ fontSize: 11.5, color: "#9e9e9e", pl: "2px", display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: "#c4c4c4" }} />
                    {formatOptionalSignedPercent(vsPreviousPercent(counts, index))} · {formatLeadCount(lost)} lost at
                    this step
                  </Box>
                </Box>
              )}
              <Box
                onClick={() => onStageClick(index)}
                onMouseEnter={() => setHoveredIndex(index)}
                data-tracker="1154"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "8px",
                  p: "2px 8px 2px 4px",
                  m: "0 -8px 0 -4px",
                  backgroundColor: hoveredIndex === index ? "#faf5fc" : "transparent",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
                  <Box
                    component="span"
                    sx={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, backgroundColor: stage.color }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>{stage.name}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: 34, minWidth: 0 }}>
                  {/* The bar may shrink (never the value text) so Paid's
                      82% bar can't push its label out of the container. */}
                  <Box
                    sx={{
                      position: "relative",
                      height: 34,
                      borderRadius: "5px",
                      backgroundColor: stage.color,
                      width: `${barWidthPercent(count, paid)}%`,
                      flexShrink: 1,
                      minWidth: 0,
                      transition: "width .25s ease",
                    }}
                  >
                    <Tooltip
                      open={hoveredIndex === index}
                      placement="top"
                      disableHoverListener
                      disableFocusListener
                      disableTouchListener
                      slotProps={BRAND_STAGES_TOOLTIP_SLOT_PROPS}
                      title={
                        <BrandStagesTooltipContent
                          stageIndex={index}
                          counts={counts}
                          scopeLabel="All brands"
                          rangeLabel={rangeLabel}
                        />
                      }
                    >
                      <Box component="span" sx={{ position: "absolute", right: 0, top: 0, width: 0, height: "100%" }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, whiteSpace: "nowrap", flexShrink: 0 }}>
                    <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
                      {formatInteger(count)}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 12.5, color: "#757575" }}>
                      {valueLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
        {paid === 0 && (
          <Typography sx={{ mt: 2, fontSize: 13.5, color: "#9e9e9e" }}>
            No paid leads were reported in the selected range.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4.25, pt: 3.25, borderTop: "1px solid #ececec" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, mb: 0.25 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#1a1a1a" }}>Brand Stages · All Brands</Typography>
        <Typography sx={{ ml: "auto", fontSize: 12.5, color: "#9e9e9e" }}>Reported {rangeLabel}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mb: 1.75 }}>
        Each bar counts the paid leads that reached that stage. The grey line between bars is the loss at that step.
      </Typography>
      {isError && (
        <BrandStagesErrorBanner message="Couldn't load the all-brands stage counts." onRetry={onRetry} />
      )}
      {renderBody()}
    </Box>
  );
}
